import React, { useMemo, useEffect, useState } from 'react';
import StatTile from '../ui/StatTile';
import { getDNColorName } from '../../utils/dnUtils';
import { useDN } from '../../contexts/DNContext';
import { API_BASE_URL } from '../../config';
import { FaStore, FaBox, FaClipboardCheck, FaUser } from 'react-icons/fa';
import emitter from '../../emitter';
import { _t, _n } from '../../utils/i18n';
import { getAdminBadge } from '../../utils/getAdminBadge';

const DNOverview = ({
  currentUser,
  projectId,
  selectedShopSigns,
  selectedCategories,
  projectConfig,
  projectConfigLoaded,
  fetchWithAuth,
  handleTabChange
}) => {
  const [productsByShopSign, setProductsByShopSign] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const { startDate, endDate, getFormattedDates } = useDN();
  const isAdmin = currentUser?.isadmin || currentUser?.issuperadmin;
  const isGlobalStatisticsAdminOnly = projectConfig?.admin_only?.includes('global_statistics');
  const canViewGlobalStatistics = !isGlobalStatisticsAdminOnly || isAdmin;
  const showGlobalStatistics = projectConfigLoaded ? canViewGlobalStatistics : isAdmin;

  // Fonction pour récupérer les statistiques globales
  useEffect(() => {
    const fetchStats = async () => {
      if (!projectId) return;

      try {
        const { formattedStartDate, formattedEndDate } = getFormattedDates();

        // Construire la requête avec les filtres si nécessaires
        let url = `${API_BASE_URL}/api/dwh/dn/${projectId}/stats?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;

        // Ajouter les filtres d'enseignes si présents
        if (selectedShopSigns && selectedShopSigns.length > 0) {
          selectedShopSigns.forEach(sign => {
            url += `&shopSigns[]=${encodeURIComponent(sign.value)}`;
          });
        }

        // Ajouter les filtres de catégories si présents
        if (selectedCategories && selectedCategories.length > 0) {
          selectedCategories.forEach(category => {
            url += `&categories[]=${encodeURIComponent(category.value)}`;
          });
        }

        const response = await fetchWithAuth(url);

        if (response.ok) {
          const statsData = await response.json();
          setStats(statsData);
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    fetchStats();
  }, [projectId, startDate, endDate, selectedShopSigns, selectedCategories, fetchWithAuth, getFormattedDates]);

  // Fonction pour récupérer les données par enseigne
  useEffect(() => {
    const fetchProductsByShopSign = async () => {
      if (!projectId) return;

      setLoading(true);
      try {
        const { formattedStartDate, formattedEndDate } = getFormattedDates();
        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/dn/${projectId}/productsByShopSign?startDate=${formattedStartDate}&endDate=${formattedEndDate}`
        );

        if (response.ok) {
          const data = await response.json();
          setProductsByShopSign(data.products || []);
        }
      } catch (error) {
        console.error("Error fetching products by shop sign:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsByShopSign();
  }, [projectId, startDate, endDate, fetchWithAuth, getFormattedDates]);

  // Filtrer les produits en fonction des enseignes sélectionnées
  const filteredProducts = useMemo(() => {
    if (!productsByShopSign || productsByShopSign.length === 0) return [];

    // Si aucune enseigne n'est sélectionnée, utiliser toutes les enseignes
    if (!selectedShopSigns || selectedShopSigns.length === 0) {
      return productsByShopSign;
    }

    // Filtrer par enseignes sélectionnées
    const selectedShopSignValues = selectedShopSigns.map(s => s.value);
    return productsByShopSign.filter(product =>
      selectedShopSignValues.includes(product.shopSign)
    );
  }, [productsByShopSign, selectedShopSigns]);

  // Filtrer les produits en fonction des catégories sélectionnées
  const categoryFilteredProducts = useMemo(() => {
    if (!filteredProducts || filteredProducts.length === 0) return [];

    // Si aucune catégorie n'est sélectionnée, utiliser tous les produits
    if (!selectedCategories || selectedCategories.length === 0) {
      return filteredProducts;
    }

    // Filtrer par catégories sélectionnées
    const selectedCategoryValues = selectedCategories.map(c => c.value);
    return filteredProducts.filter(product =>
      selectedCategoryValues.includes(product.category)
    );
  }, [filteredProducts, selectedCategories]);

  // 1. Calculer les statistiques par catégorie en agrégeant les données
  const categoriesStats = useMemo(() => {
    if (!filteredProducts || filteredProducts.length === 0) return [];

    // Regrouper les produits par catégorie
    const categoriesMap = filteredProducts.reduce((acc, product) => {
      const category = product.category || 'Sans catégorie';

      if (!acc[category]) {
        acc[category] = {
          name: category,
          products: [],
          presentShops: 0,
          notPresentShops: 0,
          prevPresentShops: 0,
          prevNotPresentShops: 0,
          productNames: new Set() // Pour compter les produits uniques
        };
      }

      acc[category].presentShops += product.presentShops;
      acc[category].notPresentShops += product.notPresentShops;
      acc[category].prevPresentShops += product.prev_presentShops;
      acc[category].prevNotPresentShops += product.prev_notPresentShops;
      acc[category].productNames.add(product.productName);
      acc[category].products.push(product);

      return acc;
    }, {});

    // Convertir la map en tableau et calculer les DN
    return Object.values(categoriesMap)
      .map(category => {
        const totalShops = category.presentShops + category.notPresentShops;
        const prevTotalShops = category.prevPresentShops + category.prevNotPresentShops;

        const averageDN = totalShops > 0
          ? (category.presentShops / totalShops * 100).toFixed(1)
          : '0.0';

        const prevAverageDN = prevTotalShops > 0
          ? (category.prevPresentShops / prevTotalShops * 100).toFixed(1)
          : '0.0';

        return {
          ...category,
          averageDN,
          prevAverageDN,
          productCount: category.productNames.size
        };
      })
      .sort((a, b) => b.averageDN - a.averageDN); // Trier par DN moyenne décroissante
  }, [filteredProducts]);

  // 2. Calculer les statistiques par enseigne en agrégeant les données
  const shopSignStats = useMemo(() => {
    if (!categoryFilteredProducts || categoryFilteredProducts.length === 0) return [];

    // Regrouper les produits par enseigne
    const shopSignsMap = categoryFilteredProducts.reduce((acc, product) => {
      const shopSign = product.shopSign || 'Non spécifié';

      if (!acc[shopSign]) {
        acc[shopSign] = {
          name: shopSign,
          products: [],
          presentShops: 0,
          notPresentShops: 0,
          prevPresentShops: 0,
          prevNotPresentShops: 0,
          productNames: new Set() // Pour compter les produits uniques
        };
      }

      acc[shopSign].presentShops += product.presentShops;
      acc[shopSign].notPresentShops += product.notPresentShops;
      acc[shopSign].prevPresentShops += product.prev_presentShops;
      acc[shopSign].prevNotPresentShops += product.prev_notPresentShops;
      acc[shopSign].productNames.add(product.productName);
      acc[shopSign].products.push(product);

      return acc;
    }, {});

    // Convertir la map en tableau et calculer les DN
    return Object.values(shopSignsMap)
      .map(shopSign => {
        const totalShops = shopSign.presentShops + shopSign.notPresentShops;
        const prevTotalShops = shopSign.prevPresentShops + shopSign.prevNotPresentShops;

        const averageDN = totalShops > 0
          ? (shopSign.presentShops / totalShops * 100).toFixed(1)
          : '0.0';

        const prevAverageDN = prevTotalShops > 0
          ? (shopSign.prevPresentShops / prevTotalShops * 100).toFixed(1)
          : '0.0';

        return {
          ...shopSign,
          averageDN,
          prevAverageDN,
          productCount: shopSign.productNames.size
        };
      })
      .sort((a, b) => b.averageDN - a.averageDN); // Trier par DN moyenne décroissante
  }, [categoryFilteredProducts]);

  // Filtrer les catégories en fonction de la sélection
  const filteredCategoriesStats = useMemo(() => {
    if (!selectedCategories || selectedCategories.length === 0) {
      return categoriesStats; // Afficher toutes les catégories si aucune n'est sélectionnée
    }

    const selectedCategoryValues = selectedCategories.map(c => c.value);
    return categoriesStats.filter(category =>
      selectedCategoryValues.includes(category.name)
    );
  }, [categoriesStats, selectedCategories]);

  // Filtrer les enseignes en fonction de la sélection
  const filteredShopSignStats = useMemo(() => {
    if (!selectedShopSigns || selectedShopSigns.length === 0) {
      return shopSignStats; // Afficher toutes les enseignes si aucune n'est sélectionnée
    }

    const selectedShopSignValues = selectedShopSigns.map(s => s.value);
    return shopSignStats.filter(shopSign =>
      selectedShopSignValues.includes(shopSign.name)
    );
  }, [shopSignStats, selectedShopSigns]);

  if (loading) {
    return (
      <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {filteredCategoriesStats.length === 0 && selectedCategories?.length === 0 && (
        <div className="flex flex-col items-center justify-center text-center py-8">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
            {new Date(endDate) > new Date() ?
              _t("No products recorded for this period yet") :
              _t("No products recorded for this period")
            }
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {_t('No distribution data is available for the selected period')}
          </p>
        </div>
      )}

      {filteredCategoriesStats.length > 0 && (
        <div className="flex flex-col lg:flex-row gap-4">
          {showGlobalStatistics && (
            <div className="w-full lg:w-[300px] flex-shrink-0">
              {stats && (
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                  <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4 text-left">
                    {_t('Global statistics')} {isGlobalStatisticsAdminOnly && isAdmin && getAdminBadge()}
                  </h2>
                  <div className="flex flex-col gap-4">
                    <StatTile
                      label={_t('Measures')}
                      value={stats.current.totalMeasures}
                      previousValue={stats.previous.totalMeasures}
                      icon={<FaClipboardCheck className="h-5 w-5" />}
                      color="indigo"
                      size="s"
                    />
                    <StatTile
                      label={_t('Field Force')}
                      value={stats.current.totalUsers}
                      previousValue={stats.previous.totalUsers}
                      icon={<FaUser className="h-5 w-5" />}
                      color="indigo"
                      size="s"
                    />
                    <StatTile
                      label={_t('Visits')}
                      value={stats.current.totalVisits}
                      previousValue={stats.previous.totalVisits}
                      icon={<FaClipboardCheck className="h-5 w-5" />}
                      size="s"
                    />
                    <StatTile
                      label={_t('Products')}
                      value={stats.current.distinctProducts}
                      previousValue={stats.previous.distinctProducts}
                      icon={<FaBox className="h-5 w-5" />}
                      color="blue"
                      size="s"
                    />
                    <StatTile
                      label={_t('Sales points')}
                      value={stats.current.distinctInterventionPoints}
                      previousValue={stats.previous.distinctInterventionPoints}
                      icon={<FaStore className="h-5 w-5" />}
                      color="green"
                      size="s"
                    />
                    <StatTile
                      label={_t('References per store')}
                      value={`${(stats.current.totalMeasures / stats.current.distinctInterventionPoints).toFixed(1)}`}
                      previousValue={`${(stats.previous.totalMeasures / stats.previous.distinctInterventionPoints).toFixed(1)}`}
                      icon={<FaStore className="h-5 w-5" />}
                      color="green"
                      size="s"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sections Catégories et Enseignes - pleine largeur sur mobile et desktop */}
          <div className="flex flex-col w-full gap-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
              <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4 text-left">
                {_t('Distribution by family')} {selectedShopSigns?.length > 0 ? `(${selectedShopSigns.map(s => s.value).join(', ')})` : ''}
              </h2>

              {filteredCategoriesStats.length === 0 && selectedCategories?.length > 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-8">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {_t('No corresponding family')}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {_t('The selected families do not correspond to any available data')}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {filteredCategoriesStats.map((category) => (
                    <StatTile
                      key={category.name}
                      label={category.name}
                      value={`${category.averageDN}%`}
                      previousValue={`${category.prevAverageDN}%`}
                      subValue={`(${category.productCount} ${_n('product', 'products', category.productCount)})`}
                      color={getDNColorName(parseFloat(category.averageDN))}
                      size="s"
                      onClick={() => {
                        emitter.emit('selectedCategory', category);
                        handleTabChange('dn_list');
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
              <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4 text-left">
                {_t('Distribution by shop sign')} {selectedCategories?.length > 0 ? `(${selectedCategories.map(c => c.value).join(', ')})` : ''}
              </h2>

              {filteredShopSignStats.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-8">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {_t('No corresponding shop sign')}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {_t('The selected shop signs do not correspond to any available data')}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {filteredShopSignStats.map((shopSign) => (
                    <StatTile
                      key={shopSign.name}
                      label={shopSign.name}
                      value={`${shopSign.averageDN}%`}
                      previousValue={`${shopSign.prevAverageDN}%`}
                      subValue={`(${shopSign.productCount} ${_n('product', 'products', shopSign.productCount)})`}
                      color={getDNColorName(parseFloat(shopSign.averageDN))}
                      size="s"
                      onClick={() => {
                        emitter.emit('selectedShopSign', shopSign);
                        handleTabChange('dn_list');
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DNOverview;